import { httpClient, IResponseMessage } from '@/core';
import {
  IUserLogin,
  ILoginResponse,
  IUserRegister,
  IForgotPassword,
  IChangePassword,
  IResendActivationCode,
  IValidateAccount,
  ILogin2faRequest,
  IMagicLinkRequest,
  ISocialRegisterRequest,
} from '../../domain/interfaces/auth.interface';
import { ISocialLoginRequest } from '../../domain/interfaces/social-auth.interface';
import { AuthRepository } from '../../domain/repositories/auth.repository';
import { ChangePasswordDto } from '../dtos/change-password.dto';
import { ForgotPasswordDto } from '../dtos/forgot-password.dto';
import { ILoginResponseDto } from '../dtos/login.dto';
import { generateStrongPassword } from '@/lib/utils';
import { RegisterRequestDto } from '../dtos/register.dto';
import { AuthMapper } from '../mappers/auth.mapper';
import { logger } from '@/core/utils/logger';

export class ApiRestDataSource implements AuthRepository {
  constructor() {}

  async login(user: IUserLogin): Promise<ILoginResponse> {
    try {
      const authHeader = this._generateAuthHeader(user);
      const response = await httpClient.post<IUserLogin, ILoginResponseDto>('/auth/signin', user, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader,
        },
      });

      return AuthMapper.toLoginResponseDto(response.data);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error during login:', normalizedError, { module: 'auth', action: 'apiLogin' });
      throw error;
    }
  }

  async loginWithToken(token: string): Promise<ILoginResponse> {
    try {
      const response = await httpClient.get<ILoginResponseDto>('/auth/verify', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      logger.debug('Token verification response', { module: 'api-rest-datasource', data: response.data });

      return AuthMapper.toLoginResponseDto(response.data);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error during login with token:', normalizedError, { module: 'auth', action: 'apiLoginWithToken' });
      throw error;
    }
  }

  /**
   * Completa el flujo de login cuando el backend pidio 2FA.
   * POST /auth/signin/2fa con `{email, code}` -> retorna JWT.
   */
  async loginWith2fa(data: ILogin2faRequest): Promise<ILoginResponse> {
    try {
      const response = await httpClient.post<ILogin2faRequest, ILoginResponseDto>('/auth/signin/2fa', data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return AuthMapper.toLoginResponseDto(response.data);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error during 2FA login:', normalizedError, { module: 'auth', action: 'apiLogin2fa' });
      throw error;
    }
  }

  /**
   * Solicita un magic link por email para login passwordless.
   * POST /auth/signin/magic.
   */
  async sendMagicLink(data: IMagicLinkRequest): Promise<IResponseMessage> {
    try {
      const response = await httpClient.post<IMagicLinkRequest, IResponseMessage>('/auth/signin/magic', data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error requesting magic link:', normalizedError, { module: 'auth', action: 'apiMagicLink' });
      throw error;
    }
  }

  /**
   * Registra un nuevo usuario via provider social (Google/Microsoft).
   * POST /auth/register/social. Retorna JWT directo (email auto-verificado).
   */
  async socialRegister(data: ISocialRegisterRequest): Promise<ILoginResponse> {
    try {
      const response = await httpClient.post<ISocialRegisterRequest, ILoginResponseDto>('/auth/register/social', data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return AuthMapper.toLoginResponseDto(response.data);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error during social register:', normalizedError, { module: 'auth', action: 'apiSocialRegister' });
      throw error;
    }
  }

  async socialLogin(data: ISocialLoginRequest): Promise<ILoginResponse> {
    try {
      const prepareData: IUserLogin = {
        email: data.email,
        password: generateStrongPassword(),
        network: data.network,
        socialToken: data.socialToken,
      };
      const authHeader = this._generateAuthHeader(prepareData);
      const response = await httpClient.post<IUserLogin, ILoginResponseDto>('/auth/signin', prepareData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader,
        },
      });

      return AuthMapper.toLoginResponseDto(response.data);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error during social login:', normalizedError, { module: 'auth', action: 'apiSocialLogin' });
      throw error;
    }
  }

  async impersonateUser(email: string): Promise<ILoginResponse> {
    try {
      // Obtener el token del usuario actual desde las cookies
      const { default: Cookies } = await import('js-cookie');
      const currentToken = Cookies.get('auth_token');

      if (!currentToken) {
        throw new Error('No se encontró token de autenticación para realizar la impersonación');
      }

      const response = await httpClient.post<{ email: string }, ILoginResponseDto>(
        '/auth/signin/admin',
        {
          email,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      return AuthMapper.toLoginResponseDto(response.data);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error during user impersonation:', normalizedError, { module: 'auth', action: 'apiImpersonateUser' });
      throw error;
    }
  }

  async register(user: IUserRegister): Promise<IResponseMessage> {
    const registerDto: RegisterRequestDto = AuthMapper.toRegisterDto(user);

    const response = await httpClient.post<RegisterRequestDto, IResponseMessage>('/auth/register', registerDto, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  async forgotPassword(data: IForgotPassword): Promise<IResponseMessage> {
    const forgotPasswordDto: ForgotPasswordDto = {
      email: data.email,
    };

    const response = await httpClient.post<ForgotPasswordDto, IResponseMessage>('/auth/forgot-password', forgotPasswordDto, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  async resetPassword(data: IChangePassword): Promise<IResponseMessage> {
    // Solo enviamos `code` + `password`. El backend resuelve al usuario por
    // el codigo (globalmente unico), por lo que el email no es necesario.
    const changePasswordDto: ChangePasswordDto = {
      code: data.code,
      password: data.password,
    };

    const response = await httpClient.post<ChangePasswordDto, IResponseMessage>('/auth/reset-password', changePasswordDto, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  async resendActivationCode(email: string): Promise<IResponseMessage> {
    const response = await httpClient.post<IResendActivationCode, IResponseMessage>(
      '/auth/resend-activation-code',
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  }

  async validateAccount(data: IValidateAccount): Promise<IResponseMessage> {
    const response = await httpClient.post<IValidateAccount, IResponseMessage>('/auth/activate-account', data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  private _generateAuthHeader(user: IUserLogin): string {
    const { email, password, network, socialToken } = user;
    return `Basic ${btoa(network && socialToken ? `${email}:${password}:${network}:${socialToken}` : `${email}:${password}`)}`;
  }
}
